import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react'
import { User, Session, AuthChangeEvent } from '@supabase/supabase-js'
import { supabase } from './supabase'
import { getUserProfile } from './api/api'
import { useInactivityTimeout } from '../hooks/use-inactivity-timeout'
import { toast } from 'sonner'
import { InactivityWarningToast } from '../components/inactivity-warning-toast'

interface AuthContextType {
  isAuthenticated: boolean
  user: User | null
  session: Session | null
  isBanned: boolean
  bannedUntil: string | null
  isTOTPVerified: boolean
  setIsTOTPVerified: (verified: boolean) => void
  login: (email: string, password: string) => Promise<void>
  signup: (email: string, password: string) => Promise<{ session: Session | null, user: User | null }>
  logout: () => Promise<void>
  refreshSession: () => Promise<void>
  getToken: () => string | null
  checkBanStatus: () => Promise<void>
  // Configuration
  inactivityTimeoutMs: number
  warningTimeMs: number
}

interface AuthProviderProps {
  children: React.ReactNode
  // Optional configuration for testing
  inactivityTimeoutMs?: number // Default: 30 minutes
  warningTimeMs?: number // Default: 1 minute
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children, inactivityTimeoutMs = 30 * 60 * 1000, warningTimeMs = 60 * 1000 }: AuthProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [isBanned, setIsBanned] = useState(false)
  const [bannedUntil, setBannedUntil] = useState<string | null>(null)
  const [isTOTPVerified, setIsTOTPVerified] = useState(false)
  const warningToastIdRef = useRef<string | number | null>(null)

  // Configurable timeouts - defaults to 30 minutes and 1 minute warning
  const INACTIVITY_TIMEOUT = inactivityTimeoutMs
  const WARNING_TIME = warningTimeMs

  console.log('[AuthProvider] Configured timeouts:', {
    inactivityTimeoutMs: INACTIVITY_TIMEOUT,
    warningTimeMs: WARNING_TIME,
    inactivityTimeoutMinutes: INACTIVITY_TIMEOUT / (60 * 1000),
    warningTimeSeconds: WARNING_TIME / 1000
  })

  const logout = async () => {
    try {
      console.log('[AuthContext] Starting logout process')

      // Dismiss any warning toast
      if (warningToastIdRef.current) {
        toast.dismiss(warningToastIdRef.current)
        warningToastIdRef.current = null
      }

      // Clear Supabase session
      const { error } = await supabase.auth.signOut()
      if (error) {
        console.warn('Error during signOut:', error.message)
      }

      // Always clear local state regardless of API errors
      setIsAuthenticated(false)
      setUser(null)
      setSession(null)
      setIsTOTPVerified(false)

      // Clear any remaining authentication-related data from localStorage
      try {
        const keysToRemove = []
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i)
          if (key && (key.startsWith('incept5-auth') || key.startsWith('supabase'))) {
            keysToRemove.push(key)
          }
        }
        keysToRemove.forEach(key => {
          console.log('[AuthContext] Removing localStorage key:', key)
          localStorage.removeItem(key)
        })
      } catch (storageError) {
        console.warn('Error clearing localStorage:', storageError)
      }

      // Clear sessionStorage as well
      try {
        const sessionKeysToRemove = []
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i)
          if (key && (key.startsWith('incept5-auth') || key.startsWith('supabase'))) {
            sessionKeysToRemove.push(key)
          }
        }
        sessionKeysToRemove.forEach(key => {
          console.log('[AuthContext] Removing sessionStorage key:', key)
          sessionStorage.removeItem(key)
        })
      } catch (storageError) {
        console.warn('Error clearing sessionStorage:', storageError)
      }

      console.log('[AuthContext] Logout process completed')
    } catch (error) {
      console.error('Exception during logout:', error)
      // Still clear local state even if there's an exception
      setIsAuthenticated(false)
      setUser(null)
      setSession(null)
      setIsTOTPVerified(false)
    }
  }

  // Handle inactivity timeout warning
  const handleInactivityWarning = (secondsRemaining: number) => {
    console.log('[AuthContext] Session warning:', secondsRemaining, 'seconds remaining')

    // Only create the toast once when the warning period starts
    if (!warningToastIdRef.current) {
      const handleStayLoggedIn = () => {
        // User activity will automatically reset the timeout
        // Just dismiss the toast
        if (warningToastIdRef.current) {
          toast.dismiss(warningToastIdRef.current)
          warningToastIdRef.current = null
        }
      }

      warningToastIdRef.current = toast(
        <InactivityWarningToast
          initialSeconds={secondsRemaining}
          onStayLoggedIn={handleStayLoggedIn}
        />,
        {
          duration: Infinity, // Keep toast open until manually dismissed
          className: 'warning-toast'
        }
      )
    }
    // Don't do anything for subsequent calls - let the component handle the countdown
  }

  // Handle inactivity timeout
  const handleInactivityTimeout = async () => {
    console.log('[AuthContext] Session expired due to inactivity, logging out')

    // Dismiss warning toast if it's still showing
    if (warningToastIdRef.current) {
      toast.dismiss(warningToastIdRef.current)
      warningToastIdRef.current = null
    }

    await logout()

    // Force redirect to login page after logout
    window.location.hash = '#/auth/login'
  }

  // Create a custom activity handler that clears warning toast
  const handleActivity = useCallback(() => {
    // Clear any existing warning toast when activity is detected
    if (warningToastIdRef.current) {
      console.log('[AuthContext] User activity detected, dismissing warning toast')
      toast.dismiss(warningToastIdRef.current)
      warningToastIdRef.current = null
    }
  }, [])

  // Use inactivity timeout hook - active when user is authenticated (regardless of TOTP status)
  // This ensures that even if TOTP verification gets stuck, the user will still be logged out
  const { resetTimeout } = useInactivityTimeout({
    timeout: INACTIVITY_TIMEOUT,
    onTimeout: handleInactivityTimeout,
    onWarning: handleInactivityWarning,
    warningTime: WARNING_TIME,
    isActive: isAuthenticated
  })

  // Override resetTimeout to include toast cleanup
  useEffect(() => {
    if (!isAuthenticated) return

    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'keydown'
    ]

    const activityHandler = () => {
      handleActivity()
      resetTimeout()
    }

    events.forEach(event => {
      document.addEventListener(event, activityHandler, { passive: true })
    })

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, activityHandler)
      })
    }
  }, [isAuthenticated, handleActivity, resetTimeout])

  useEffect(() => {
    // Check active sessions and sets the user
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsAuthenticated(!!session)
      setUser(session?.user ?? null)
      setSession(session)
      // Reset TOTP verification on new session
      setIsTOTPVerified(false)
    })

    // Listen for changes on auth state (logged in, signed out, etc.)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event: AuthChangeEvent, session: Session | null) => {
        console.log('[AuthContext] Auth state change:', event)
        setIsAuthenticated(!!session)
        setUser(session?.user ?? null)
        setSession(session)

        // Handle logout/signed out events
        if (event === 'SIGNED_OUT' && !session) {
          console.log('[AuthContext] User signed out, clearing state and redirecting')
          setIsAuthenticated(false)
          setUser(null)
          setSession(null)
          setIsTOTPVerified(false)

          // Force redirect to login page if not already there
          if (window.location.hash !== '#/auth/login' && !window.location.hash.startsWith('#/auth/')) {
            console.log('[AuthContext] Redirecting to login page after logout')
            window.location.hash = '#/auth/login'
          }
        }

        // Always reset TOTP verification on any auth state change except manual verification
        if (event === 'SIGNED_OUT' || event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          console.log('[AuthContext] Resetting TOTP verification due to auth state change:', event)
          setIsTOTPVerified(false)
        }
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  const refreshSession = async () => {
    const { data: { session }, error } = await supabase.auth.refreshSession()
    if (error) throw error
    setSession(session)
    setUser(session?.user ?? null)
    setIsAuthenticated(!!session)
  }

  const getToken = (): string | null => {
    return session?.access_token ?? null
  }

  const login = async (email: string, password: string) => {
    console.log('[AuthContext] Starting login process')
    // Always reset TOTP verification on new login attempts
    setIsTOTPVerified(false)

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    if (error) throw error

    // Explicitly set the session to ensure it's available for MFA operations
    if (data.session) {
      console.log('[AuthContext] Setting session after password authentication')
      setSession(data.session)
      setUser(data.user)
      setIsAuthenticated(true)
    }

    console.log('[AuthContext] Password authentication successful, TOTP verification required')
  }

  const signup = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    })
    if (error) throw error
    return { session: data.session, user: data.user }
  }

  const checkBanStatus = async () => {
    if (!user || !isTOTPVerified) {
      console.log('[AuthContext] Skipping ban status check - user not fully authenticated', { hasUser: !!user, isTOTPVerified })
      return
    }
    try {
      console.log('[AuthContext] Checking ban status for fully authenticated user')
      const userProfile = await getUserProfile()
      const isBannedNow = !!userProfile.bannedUntil && new Date(userProfile.bannedUntil) > new Date()
      setIsBanned(isBannedNow)
      setBannedUntil(userProfile.bannedUntil)

      if (isBannedNow) {
        console.log('[AuthContext] User is banned, logging out')
        await logout()
      }
    } catch (error) {
      console.error('Error checking ban status:', error)
    }
  }

  useEffect(() => {
    if (user && isTOTPVerified) {
      checkBanStatus()
    }
  }, [user, isTOTPVerified])

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      user,
      session,
      isBanned,
      bannedUntil,
      isTOTPVerified,
      setIsTOTPVerified,
      login,
      signup,
      logout,
      refreshSession,
      getToken,
      checkBanStatus,
      inactivityTimeoutMs: INACTIVITY_TIMEOUT,
      warningTimeMs: WARNING_TIME
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
